import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { normalizeFeedViewMode } from './feedViewMode';
import { resolveDiscussionAssetUrl } from './discussionUtils';
import { getFeedScrollElement } from './feedScrollRestore';

import { isPostMediaVideoFile } from './postMediaFiles';

export function isDiscussionMediaUrlProbablyVideo(url) {
  return /\.(mp4|webm|mov)(\?|#|$)/i.test(String(url || ''));
}

/**
 * Resolved media items for a thread (feed thumb + lightbox). Video items include optional poster URL.
 * @returns {{ url: string, type: 'image'|'video', posterUrl: string }[]}
 */
/**
 * Existing post media for the edit-post modal (raw URLs for API; `removed` toggles per item).
 * @returns {{ id: string, type: 'image'|'video', url: string, posterUrl: string, removed: boolean }[]}
 */
export function buildDiscussionEditExistingMedia(thread) {
  if (!thread) return [];
  const items = [];
  const mt = String(thread.mediaType || '').toLowerCase();
  let idx = 0;

  if (mt === 'video' && thread.mediaUrl) {
    items.push({
      id: `existing-media-${idx++}`,
      type: 'video',
      url: thread.mediaUrl,
      posterUrl: thread.mediaThumbUrl || '',
      removed: false,
    });
  }

  if (Array.isArray(thread.mediaUrls) && thread.mediaUrls.length) {
    thread.mediaUrls.forEach((u) => {
      if (!u) return;
      items.push({
        id: `existing-media-${idx++}`,
        type: 'image',
        url: u,
        posterUrl: '',
        removed: false,
      });
    });
  } else if (mt === 'image' && thread.mediaUrl) {
    items.push({
      id: `existing-media-${idx++}`,
      type: 'image',
      url: thread.mediaUrl,
      posterUrl: '',
      removed: false,
    });
  }

  if (!items.length && thread.mediaThumbUrl) {
    items.push({
      id: `existing-media-${idx++}`,
      type: 'image',
      url: thread.mediaThumbUrl,
      posterUrl: '',
      removed: false,
    });
  }

  return items;
}

/**
 * Build media fields for discussionsUpdate after the user edited attachments.
 */
export async function buildDiscussionEditMediaUpdatePayload({
  existingMedia,
  newMediaFiles,
  uploadMedia,
  onUploadProgress,
}) {
  const keptExisting = (existingMedia || []).filter((m) => !m.removed);
  const uploadedMedia = [];
  for (const mediaFile of newMediaFiles || []) {
    const uploaded = await uploadMedia(mediaFile, { onProgress: onUploadProgress });
    if (uploaded?.url) uploadedMedia.push(uploaded);
  }
  const uploadedImageItems = uploadedMedia
    .filter((m) => String(m?.mediaType || '').toLowerCase() === 'image' && m?.url)
    .map((m) => ({ type: 'image', url: m.url, posterUrl: '' }));
  const uploadedVideoItems = uploadedMedia
    .filter((m) => String(m?.mediaType || '').toLowerCase() === 'video' && m?.url)
    .map((m) => ({ type: 'video', url: m.url, posterUrl: m.thumbUrl || '' }));
  const finalVideos = [
    ...keptExisting.filter((m) => m.type === 'video' && m.url),
    ...uploadedVideoItems,
  ];
  const finalImages = [
    ...keptExisting.filter((m) => m.type === 'image' && m.url),
    ...uploadedImageItems,
  ];
  const primaryVideo = finalVideos[0] || null;
  const primaryImage = finalImages[0] || null;
  return {
    mediaType: primaryVideo ? 'video' : primaryImage ? 'image' : '',
    mediaUrl: primaryVideo?.url || primaryImage?.url || '',
    mediaThumbUrl: primaryVideo?.posterUrl || '',
    mediaUrls: finalImages.map((m) => m.url),
  };
}

export function discussionThreadMediaGallery(thread) {
  if (!thread) return [];
  const items = [];
  const mt = String(thread.mediaType || '').toLowerCase();
  if (mt === 'video' && thread.mediaUrl) {
    const poster = thread.mediaThumbUrl ? resolveDiscussionAssetUrl(thread.mediaThumbUrl) : '';
    items.push({
      url: resolveDiscussionAssetUrl(thread.mediaUrl),
      type: 'video',
      posterUrl: poster,
    });
  }
  if (Array.isArray(thread.mediaUrls) && thread.mediaUrls.length) {
    thread.mediaUrls.forEach((u) => {
      if (!u) return;
      items.push({
        url: resolveDiscussionAssetUrl(u),
        type: 'image',
        posterUrl: '',
      });
    });
    return items;
  }
  if (mt === 'image' && thread.mediaUrl) {
    items.push({ url: resolveDiscussionAssetUrl(thread.mediaUrl), type: 'image', posterUrl: '' });
    return items;
  }
  if (items.length) return items;
  if (thread.mediaThumbUrl) {
    return [{ url: resolveDiscussionAssetUrl(thread.mediaThumbUrl), type: 'image', posterUrl: '' }];
  }
  return [];
}

/** True when the thread has at least one image or video attachment. */
export function threadHasDiscussionMedia(thread) {
  return discussionThreadMediaGallery(thread).length > 0;
}

/** Slider items for a thread: videos first, then images. */
export function discussionThreadMediaSliderItems(thread, { title = '' } = {}) {
  return discussionThreadMediaGallery(thread).map((item, index) => ({
    id: `${thread?.slug || 'thread'}-media-${index}`,
    type: item.type,
    url: item.url,
    posterUrl: item.posterUrl || '',
    alt:
      item.type === 'video'
        ? title
          ? `${title} video`
          : 'Post video'
        : title
          ? `${title} image ${index + 1}`
          : `Post image ${index + 1}`,
  }));
}

/** Compose-time files in slider order (videos first, then images). */
export function discussionComposeMediaSliderItems(files = [], previewUrls = []) {
  const items = (files || []).map((file, index) => {
    const isVideo = isPostMediaVideoFile(file);
    return {
      id: `compose-media-${index}`,
      originalIndex: index,
      type: isVideo ? 'video' : 'image',
      url: previewUrls[index] || '',
      posterUrl: '',
      alt: isVideo ? 'Video preview' : `Image preview ${index + 1}`,
    };
  });
  return items
    .filter((item) => item.url)
    .sort((a, b) => {
      if (a.type === b.type) return a.originalIndex - b.originalIndex;
      return a.type === 'video' ? -1 : 1;
    });
}

/**
 * Whether a feed thumbnail click should navigate to the post (true) or open the media lightbox (false).
 * List layout uses the lightbox; card and modern layouts open the discussion thread.
 * @param {unknown} _mediaGallery
 * @param {string|boolean} [feedViewModeOrLegacyFlag='list'] feed view mode, or legacy boolean (treated as list)
 */
export function feedMediaOpensDiscussionThread(_mediaGallery, feedViewModeOrLegacyFlag = 'list') {
  const feedViewMode =
    typeof feedViewModeOrLegacyFlag === 'boolean' ? 'list' : feedViewModeOrLegacyFlag;
  return normalizeFeedViewMode(feedViewMode) !== 'list';
}

export {
  extractBodyMediaFromHtml as extractDiscussionBodyMediaFromHtml,
  bodyMediaSrcMatches as discussionBodyMediaSrcMatches,
} from '../../../../layout/ReusableComponent/Text-editor/textEditorBodyUtils';

export function normalizeDiscussionMediaModalItems(itemsIn) {
  if (!itemsIn || !itemsIn.length) return [];
  const first = itemsIn[0];
  if (typeof first === 'string') {
    return itemsIn.filter(Boolean).map((url) => ({
      url,
      type: isDiscussionMediaUrlProbablyVideo(url) ? 'video' : 'image',
      posterUrl: '',
    }));
  }
  return itemsIn
    .filter((x) => x && x.url)
    .map((x) => ({
      url: x.url,
      type: x.type === 'video' ? 'video' : 'image',
      posterUrl: x.posterUrl || '',
      alt: x.alt || '',
    }));
}

/**
 * Feed card video thumb: mobile Safari often skips painting a frame with preload="metadata" only.
 * Media Fragment + a tiny seek after load nudges WebKit/Blink to decode the first frame.
 */
export function discussionVideoCardThumbSrc(url) {
  const u = String(url || '').trim();
  if (!u || u.includes('#')) return u;
  return `${u}#t=0.001`;
}

const FEED_VIDEO_VIEWPORT_THRESHOLD = 0.4;
const FEED_VIDEO_SCROLL_END_MS = 180;

const feedVideoScrollListeners = new Set();
const feedVideoScrollRoots = new Set();
let feedVideoIsScrolling = false;
let feedVideoScrollEndTimer = null;
let feedVideoScrollListenerAttached = false;

function isFeedVideoScrollPane(el) {
  if (!(el instanceof HTMLElement)) return false;
  const { overflowY } = window.getComputedStyle(el);
  const allowsScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  return allowsScroll && el.scrollHeight > el.clientHeight + 1;
}

function findFeedVideoScrollParent(el) {
  let node = el?.parentElement;
  while (node) {
    if (isFeedVideoScrollPane(node)) return node;
    node = node.parentElement;
  }
  return null;
}

function notifyFeedVideoScrollPause() {
  if (!feedVideoIsScrolling) {
    feedVideoIsScrolling = true;
    feedVideoScrollListeners.forEach((fn) => fn(true));
  }
  if (feedVideoScrollEndTimer) window.clearTimeout(feedVideoScrollEndTimer);
  feedVideoScrollEndTimer = window.setTimeout(() => {
    feedVideoIsScrolling = false;
    feedVideoScrollListeners.forEach((fn) => fn(false));
  }, FEED_VIDEO_SCROLL_END_MS);
}

function registerFeedVideoScrollRoot(el) {
  if (!el || feedVideoScrollRoots.has(el)) return;
  feedVideoScrollRoots.add(el);
  el.addEventListener('scroll', notifyFeedVideoScrollPause, { passive: true });
}

function attachFeedVideoScrollListener() {
  if (feedVideoScrollListenerAttached || typeof window === 'undefined') return;
  feedVideoScrollListenerAttached = true;
  window.addEventListener('scroll', notifyFeedVideoScrollPause, { passive: true, capture: true });
  registerFeedVideoScrollRoot(getFeedScrollElement());
}

function useFeedVideoScrollPaused(videoRef, mountKey) {
  const [paused, setPaused] = useState(feedVideoIsScrolling);
  useEffect(() => {
    attachFeedVideoScrollListener();
    registerFeedVideoScrollRoot(getFeedScrollElement());
    registerFeedVideoScrollRoot(findFeedVideoScrollParent(videoRef.current));
    feedVideoScrollListeners.add(setPaused);
    return () => feedVideoScrollListeners.delete(setPaused);
  }, [videoRef, mountKey]);
  return paused;
}

function resetFeedVideoToThumb(el, poster) {
  if (!el) return;
  el.pause();
  try {
    if (poster) {
      el.currentTime = 0;
      return;
    }
    if (el.readyState < 2) return;
    const dur = el.duration;
    if (Number.isFinite(dur) && dur > 0) {
      el.currentTime = Math.min(0.125, dur * 0.02);
    } else {
      el.currentTime = 0.001;
    }
  } catch {
    /* ignore */
  }
}

/**
 * Video shown after the user opens post media (modal / thread). Autoplays when `active`.
 */
export function DiscussionAutoplayVideo({
  src,
  poster,
  className,
  active = true,
  onLoadedMetadata,
  onLoadedData,
  onCanPlay,
  ...rest
}) {
  const ref = useRef(null);

  const tryPlay = useCallback(() => {
    if (!active) return;
    const el = ref.current;
    if (!el) return;
    const promise = el.play();
    if (promise?.catch) promise.catch(() => {});
  }, [active]);

  useLayoutEffect(() => {
    if (!active) return;
    tryPlay();
  }, [active, src, tryPlay]);

  useEffect(() => {
    const el = ref.current;
    if (!active && el) {
      el.pause();
      try {
        el.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
  }, [active, src]);

  useEffect(() => {
    if (!active) return;
    const el = ref.current;
    if (el) el.muted = true;
  }, [active, src]);

  const handleLoadedData = useCallback(
    (e) => {
      onLoadedData?.(e);
      tryPlay();
    },
    [onLoadedData, tryPlay]
  );

  const handleCanPlay = useCallback(
    (e) => {
      onCanPlay?.(e);
      tryPlay();
    },
    [onCanPlay, tryPlay]
  );

  if (!src) return null;

  return (
    <video
      ref={ref}
      className={className}
      controls
      playsInline
      loop
      autoPlay
      defaultMuted
      preload="auto"
      src={src}
      poster={poster || undefined}
      onLoadedMetadata={onLoadedMetadata}
      onLoadedData={handleLoadedData}
      onCanPlay={handleCanPlay}
      {...rest}
    />
  );
}

export function OpenDiscFeedVideoThumb({ src, poster, className, onLoadedMetadata, onLoadedData, ...rest }) {
  const ref = useRef(null);
  const inViewRef = useRef(false);
  const shouldPlayRef = useRef(false);
  const vSrc = useMemo(() => discussionVideoCardThumbSrc(src), [src]);
  const [isHovered, setIsHovered] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [pausedAfterMouseLeave, setPausedAfterMouseLeave] = useState(false);
  const isScrollPaused = useFeedVideoScrollPaused(ref, vSrc);
  const shouldPlay = !isScrollPaused && (isHovered || (isInView && !pausedAfterMouseLeave));

  useEffect(() => {
    shouldPlayRef.current = shouldPlay;
  }, [shouldPlay]);

  useEffect(() => {
    inViewRef.current = isInView;
  }, [isInView]);

  useEffect(() => {
    if (!isScrollPaused) {
      setPausedAfterMouseLeave(false);
    }
  }, [isScrollPaused]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const visible =
          Boolean(entry?.isIntersecting) &&
          (entry.intersectionRatio >= FEED_VIDEO_VIEWPORT_THRESHOLD || entry.intersectionRatio === 1);
        setIsInView(visible);
        if (!visible) {
          setPausedAfterMouseLeave(false);
        }
      },
      { threshold: [0, FEED_VIDEO_VIEWPORT_THRESHOLD, 0.6, 1] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [vSrc]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (shouldPlay) {
      el.muted = true;
      const promise = el.play();
      if (promise?.catch) promise.catch(() => {});
      return;
    }
    resetFeedVideoToThumb(el, poster);
  }, [shouldPlay, poster, vSrc]);

  useEffect(() => {
    if (shouldPlay || poster) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    let cancelled = false;
    const nudgeFrame = () => {
      if (cancelled || shouldPlayRef.current) return;
      resetFeedVideoToThumb(el, '');
    };
    el.addEventListener('loadedmetadata', nudgeFrame);
    el.addEventListener('loadeddata', nudgeFrame);
    el.addEventListener('canplay', nudgeFrame);
    return () => {
      cancelled = true;
      el.removeEventListener('loadedmetadata', nudgeFrame);
      el.removeEventListener('loadeddata', nudgeFrame);
      el.removeEventListener('canplay', nudgeFrame);
    };
  }, [vSrc, poster, shouldPlay]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    setPausedAfterMouseLeave(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (inViewRef.current) {
      setPausedAfterMouseLeave(true);
    }
  }, []);

  const handleLoadedMetadata = useCallback(
    (e) => {
      onLoadedMetadata?.(e);
      if (!shouldPlay) {
        resetFeedVideoToThumb(e.currentTarget, poster);
      }
    },
    [onLoadedMetadata, poster, shouldPlay]
  );

  const handleLoadedData = useCallback(
    (e) => {
      onLoadedData?.(e);
      if (!shouldPlay) {
        resetFeedVideoToThumb(e.currentTarget, poster);
      }
    },
    [onLoadedData, poster, shouldPlay]
  );

  const mergedClassName = [
    className,
    shouldPlay ? 'support-open-disc-feed-video-thumb--playing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <video
      ref={ref}
      className={mergedClassName || undefined}
      src={vSrc}
      poster={poster || undefined}
      preload="auto"
      muted
      playsInline
      loop
      tabIndex={-1}
      aria-hidden
      onContextMenu={(e) => e.preventDefault()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onLoadedMetadata={handleLoadedMetadata}
      onLoadedData={handleLoadedData}
      {...rest}
    />
  );
}
